import { useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useTrackLoadingState } from 'src/hooks/ufo';
import {
  FETCH_DIFF,
  FETCH_DIFF_FILE,
  LOAD_DIFFSTAT,
  TOGGLE_SINGLE_FILE_MODE,
  TOGGLE_SINGLE_FILE_MODE_ELIGIBILITY,
} from 'src/redux/pull-request/actions';
import { getDiff, getDiffStat } from 'src/redux/pull-request/api';
import { getIsSingleFileModeActive } from 'src/redux/pull-request/selectors';
import { addQueryParams } from 'src/redux/pull-request/urls/url-utils';
import { usePullRequestAttributes } from 'src/sections/repository/sections/pull-request/hooks/analytics';
import { DiffStat } from 'src/types/diffstat';
import { shouldTriggerSingleFileMode } from 'src/utils/diff-classifications';
import WordDiffWorker from 'src/workers/word-diff.worker';

export type FetchDiffHookOptions = {
  collectDiffFetchedAnalytics?: () => void;
  collectDiffParsedAnalytics?: () => void;
};

export function useDiff(
  diffUrlBuilder: (
    shouldIgnoreWhitespace?: boolean,
    topicDiff?: boolean,
    useForkMergebase?: boolean
  ) => string,
  diffFetchOptions: FetchDiffHookOptions,
  sourceHash: string,
  destHash?: string,
  useForkMergebase?: boolean,
  singleHashDiff?: boolean
): {
  isLoading: boolean;
  loadDiff: () => void;
  loadFileDiff: (fromPath: string, toPath: string) => void;
} {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  useTrackLoadingState('loading-diff', isLoading);

  const {
    diffSettings: {
      isWordDiffEnabled,
      shouldIgnoreWhitespace,
      isSingleFileModeEnabled,
    },
    renderingLimits,
  } = usePullRequestAttributes();

  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);

  const diffUrl = diffUrlBuilder(
    shouldIgnoreWhitespace,
    false,
    useForkMergebase
  );

  const loadDiff = useCallback(() => {
    let isCancelled = false;
    setIsLoading(true);
    if (!destHash && !singleHashDiff) {
      setIsLoading(false);
      return () => undefined;
    }
    (async () => {
      try {
        const { diff, isPartial } = await getDiff(diffUrl);

        if (diffFetchOptions.collectDiffFetchedAnalytics) {
          diffFetchOptions.collectDiffFetchedAnalytics();
        }

        const worker = WordDiffWorker();
        const diffs = await worker.processDiff(diff, {
          isWordDiffEnabled,
        });

        const isSingleFileModeThresholdMet =
          isPartial || shouldTriggerSingleFileMode(diffs, renderingLimits);

        const isForcedSingleFileMode =
          isPartial ||
          (!isSingleFileModeActive &&
            (isSingleFileModeThresholdMet || isSingleFileModeEnabled));

        if (isSingleFileModeThresholdMet) {
          dispatch({
            type: TOGGLE_SINGLE_FILE_MODE_ELIGIBILITY,
            payload: true,
          });
        }

        if (isForcedSingleFileMode) {
          dispatch({
            type: TOGGLE_SINGLE_FILE_MODE,
            payload: true,
          });
        }

        setIsLoading(false);
        if (!isCancelled) {
          dispatch({
            type: FETCH_DIFF.SUCCESS,
            payload: diffs,
          });

          if (diffFetchOptions.collectDiffParsedAnalytics) {
            diffFetchOptions.collectDiffParsedAnalytics();
          }
        }
      } catch (e) {
        setIsLoading(false);
        if (!isCancelled) {
          dispatch({
            type: FETCH_DIFF.ERROR,
            payload: e.status,
          });
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
    /*
     We include sourceHash and destHash as dependencies instead of the URL because 
    when the dest repo is changed on the create PR page, the dest repo can update
    before the new dest hash is fetched, which would result in calls with old dest hash and new dest hash
    - isSingleFileModeActive is missing because we don't want to fetch the diff
    again when we toggle modes
    - options is missing because it was causing indefinite re-runs
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isWordDiffEnabled,
    shouldIgnoreWhitespace,
    useForkMergebase,
    singleHashDiff,
    sourceHash,
    destHash,
  ]);

  // We explicitly don't want to set a loading state when fetching individual
  // file diffs. Doing so will unmount the PartialDiff component that requested
  // the load, but it will get rendered (and request a duplicate diff) a second
  // time before the actual diff is rendered. We'll let the component handle a
  // loading state.
  const loadFileDiff = (fromPath: string, toPath: string) => {
    let isCancelled = false;
    if (!destHash && !singleHashDiff) {
      return () => undefined;
    }
    // To handle renames and deletions, we need to include both the new and old
    // path (if they aren't empty)
    const paths = [fromPath, toPath].filter(path => path !== '/dev/null');
    (async () => {
      try {
        const { diff } = await getDiff(
          addQueryParams(diffUrl, {
            path: paths,
          })
        );

        const worker = WordDiffWorker();
        const diffs = await worker.processDiff(diff, {
          isWordDiffEnabled,
        });

        // If the diff is empty, we probably timed out. We need to add a mock
        // response so the ClassifiedDiff can render an "error" state and not
        // try to re-load this same file
        if (diffs.length === 0) {
          diffs.push({
            from: fromPath,
            to: toPath,
            headers: ['diff_timeout'],
            chunks: [],
            isBinary: false,
            isImage: false,
            isExcluded: false,
            deletions: 0,
            additions: 0,
            index: [],
          });
        }

        if (!isCancelled) {
          dispatch({
            type: FETCH_DIFF_FILE,
            payload: diffs,
          });
        }
      } catch (e) {
        if (!isCancelled) {
          dispatch({
            type: FETCH_DIFF.ERROR,
            payload: e.status,
          });
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
  };

  return { isLoading, loadDiff, loadFileDiff };
}

export function useDiffStat(
  diffStatUrlBuilder: (
    shouldIgnoreWhitespace?: boolean,
    useForkMergebase?: boolean
  ) => string,
  sourceHash: string,
  destHash?: string,
  useForkMergebase?: boolean,
  singleHashDiff?: boolean,
  maxPages?: number
): { isLoading: boolean; loadDiffStat: () => void } {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  useTrackLoadingState('loading-diffstat', isLoading);

  const {
    diffSettings: { shouldIgnoreWhitespace },
  } = usePullRequestAttributes();

  const diffStatUrl = diffStatUrlBuilder(
    shouldIgnoreWhitespace,
    useForkMergebase
  );

  const loadDiffStat = useCallback(() => {
    setIsLoading(true);
    let isCancelled = false;
    if (!destHash && !singleHashDiff) {
      setIsLoading(false);
      return () => undefined;
    }
    (async () => {
      try {
        let nextUrl = diffStatUrl;
        let pagesLeft = maxPages;
        let fetchMore = true;

        const values: DiffStat[] = [];

        while (nextUrl && fetchMore) {
          // eslint-disable-next-line no-await-in-loop
          const diffStat = await getDiffStat(nextUrl);
          nextUrl = diffStat.next;
          values.push(...diffStat.values);
          pagesLeft = pagesLeft === undefined ? undefined : pagesLeft - 1;
          fetchMore = (pagesLeft ?? 1) > 0;
        }

        setIsLoading(false);

        if (!isCancelled) {
          dispatch({
            type: LOAD_DIFFSTAT.SUCCESS,
            payload: { values },
          });
        }
      } catch (e) {
        setIsLoading(false);
        if (!isCancelled) {
          dispatch({
            type: LOAD_DIFFSTAT.ERROR,
            payload: {
              diffStatErrorCode: e.status,
              diffStatErrorKey: e.key,
            },
          });
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
    /*
    We include sourceHash and destHash as dependencies instead of the URL because 
    when the dest repo is changed on the create PR page, the dest repo can update
    before the new dest hash is fetched, which would result in calls with old dest hash and new dest hash
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    shouldIgnoreWhitespace,
    useForkMergebase,
    sourceHash,
    destHash,
  ]);

  return { isLoading, loadDiffStat };
}
